import {
  type AngleMode,
  type CalcError,
  closeBrackets,
  type DisplayMode,
  displayNumber,
  evaluateExpression,
  machineText,
} from "@/lib/tool-kit/arithmetic/scientific";

/**
 * The scientific calculator's state machine, kept out of the component so the behaviour a
 * hand-held calculator has — an operator after "=" continues from the answer, a digit
 * starts afresh, memory keys act on the number on screen — can be tested without a browser.
 */
export interface HistoryEntry {
  id: number;
  expression: string;
  /** The answer as shown, with × 10ⁿ for large and small values. */
  pretty: string;
  /** The answer as text the parser reads back. */
  plain: string;
}

export interface CalcSession {
  expression: string;
  selectionStart: number;
  selectionEnd: number;
  ans: number;
  memory: number;
  history: HistoryEntry[];
  /** True just after "=": the next key decides whether to continue or start over. */
  justEvaluated: boolean;
  nextId: number;
  /** The error from the last "=" press, if it failed. */
  error: CalcError | null;
}

export const INITIAL_SESSION: CalcSession = {
  expression: "",
  selectionStart: 0,
  selectionEnd: 0,
  ans: 0,
  memory: 0,
  history: [],
  justEvaluated: false,
  nextId: 1,
  error: null,
};

export interface SessionOptions {
  angle: AngleMode;
  implicitTight: boolean;
  display: DisplayMode;
}

export type KeyKind = "digit" | "operator" | "function" | "constant";

export type MemoryOperation = "clear" | "recall" | "add" | "subtract" | "store";

export type SessionAction =
  | { type: "insert"; text: string; kind: KeyKind }
  | { type: "edit"; expression: string; start: number; end: number }
  | { type: "select"; start: number; end: number }
  | { type: "backspace" }
  | { type: "clear" }
  | { type: "equals"; options: SessionOptions }
  | { type: "memory"; operation: MemoryOperation; options: SessionOptions }
  | { type: "reuse"; text: string }
  | { type: "clearHistory" };

export const MAX_HISTORY = 40;

function place(
  state: CalcSession,
  text: string,
  start: number,
  end: number
): CalcSession {
  const expression =
    state.expression.slice(0, start) + text + state.expression.slice(end);
  const caret = start + text.length;
  return {
    ...state,
    expression,
    selectionStart: caret,
    selectionEnd: caret,
    justEvaluated: false,
    error: null,
  };
}

function insertKey(
  state: CalcSession,
  text: string,
  kind: KeyKind
): CalcSession {
  if (state.justEvaluated) {
    // An operator carries on from the answer; anything else starts a new calculation.
    const fresh: CalcSession = {
      ...state,
      expression: kind === "operator" ? "ans" : "",
      selectionStart: kind === "operator" ? 3 : 0,
      selectionEnd: kind === "operator" ? 3 : 0,
      justEvaluated: false,
    };
    return place(fresh, text, fresh.selectionStart, fresh.selectionEnd);
  }
  return place(state, text, state.selectionStart, state.selectionEnd);
}

function backspace(state: CalcSession): CalcSession {
  if (state.selectionStart !== state.selectionEnd) {
    return place(state, "", state.selectionStart, state.selectionEnd);
  }
  if (state.selectionStart === 0) {
    return state;
  }
  return place(state, "", state.selectionStart - 1, state.selectionStart);
}

/** The value on screen: the live expression if it evaluates, else the last answer. */
export function currentValue(
  state: CalcSession,
  options: SessionOptions
): number {
  const result = evaluateExpression(state.expression, {
    angle: options.angle,
    ans: state.ans,
    implicitTight: options.implicitTight,
  });
  return result.ok ? result.value : state.ans;
}

function equals(state: CalcSession, options: SessionOptions): CalcSession {
  const result = evaluateExpression(state.expression, {
    angle: options.angle,
    ans: state.ans,
    implicitTight: options.implicitTight,
  });
  if (!result.ok) {
    return { ...state, error: result.error, justEvaluated: false };
  }
  const shown = displayNumber(result.value, options.display);
  const entry: HistoryEntry = {
    id: state.nextId,
    expression: closeBrackets(state.expression.trim()),
    pretty: shown.pretty,
    plain: machineText(result.value),
  };
  return {
    ...state,
    ans: result.value,
    history: [entry, ...state.history].slice(0, MAX_HISTORY),
    nextId: state.nextId + 1,
    justEvaluated: true,
    error: null,
  };
}

function memory(
  state: CalcSession,
  operation: MemoryOperation,
  options: SessionOptions
): CalcSession {
  const value = currentValue(state, options);
  switch (operation) {
    case "clear":
      return { ...state, memory: 0 };
    case "store":
      return { ...state, memory: value };
    case "add":
      return { ...state, memory: state.memory + value };
    case "subtract":
      return { ...state, memory: state.memory - value };
    default:
      return insertKey(state, machineText(state.memory), "digit");
  }
}

export function reduceSession(
  state: CalcSession,
  action: SessionAction
): CalcSession {
  switch (action.type) {
    case "insert":
      return insertKey(state, action.text, action.kind);
    case "edit":
      return {
        ...state,
        expression: action.expression,
        selectionStart: action.start,
        selectionEnd: action.end,
        justEvaluated: false,
        error: null,
      };
    case "select":
      return {
        ...state,
        selectionStart: action.start,
        selectionEnd: action.end,
      };
    case "backspace":
      return backspace(state);
    case "clear":
      return {
        ...state,
        expression: "",
        selectionStart: 0,
        selectionEnd: 0,
        justEvaluated: false,
        error: null,
      };
    case "equals":
      return equals(state, action.options);
    case "memory":
      return memory(state, action.operation, action.options);
    case "reuse":
      return insertKey(state, action.text, "digit");
    default:
      return { ...state, history: [] };
  }
}
